import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, CircularProgress, Paper, Button, Alert, Link as MuiLink } from '@mui/material';
import DOMPurify from 'dompurify'; // For sanitizing HTML

const ArticleViewer = () => {
    const { encodedUrl } = useParams();
    const navigate = useNavigate();
    const [articleContent, setArticleContent] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [originalUrl, setOriginalUrl] = useState('');

    useEffect(() => {
        let decodedUrl = '';
        try {
            decodedUrl = decodeURIComponent(encodedUrl);
            setOriginalUrl(decodedUrl);
        } catch (e) {
            setError("Invalid article URL provided.");
            setLoading(false);
            return;
        }

        const fetchArticle = async () => {
            setLoading(true);
            setError(null);
            try {
                // Fetch from the new API endpoint, passing the original URL
                const response = await fetch(`/api/fetch-article?url=${encodeURIComponent(decodedUrl)}`);
                
                if (!response.ok) {
                    let errorMsg = `Error fetching article (${response.status})`;
                    try {
                       const errData = await response.json();
                       errorMsg = errData.error || errorMsg;
                    } catch {}
                    throw new Error(errorMsg);
                }
                
                const data = await response.json();

                // Sanitize the fetched HTML content before setting state
                const cleanHtml = DOMPurify.sanitize(data.content);
                setArticleContent(cleanHtml);

            } catch (e) {
                console.error("Failed to fetch or parse article:", e);
                setError(`Failed to load article content: ${e.message}`);
            } finally {
                setLoading(false);
            }
        };

        if (decodedUrl) {
            fetchArticle();
        }

    }, [encodedUrl]);

    const handleGoBack = () => {
        // Introduce a tiny delay before navigating
        // This might occasionally help with transition-related observer errors, but it's not guaranteed.
        setTimeout(() => {
            navigate('/'); 
        }, 0); // 0ms delay pushes execution to the next event loop cycle
    };

    return (
        <Paper elevation={1} sx={{ p: 3, bgcolor: 'rgba(50,50,50,0.85)', color: 'white' }}>
            <Button 
                variant="contained" 
                color="primary"
                onClick={handleGoBack}
                sx={{ mb: 3 }}
            >
                Back to Calculator
            </Button>

            {loading && <Box sx={{ display: 'flex', justifyContent: 'center', my: 5 }}><CircularProgress /></Box>}
            {error && <Alert severity="error" sx={{ my: 3 }}>{error}</Alert>}
            
            {!loading && !error && (
                <Box>
                    {/* Render the sanitized HTML */} 
                    {/* Apply some basic styling for readability */}
                    <Box 
                        className="article-content" 
                        dangerouslySetInnerHTML={{ __html: articleContent }}
                        sx={{
                            // Ensure paragraphs and list items are left-aligned
                            '& p, & li': { textAlign: 'left', lineHeight: 1.6, my: 1.5 }, 
                            // Image styles
                            '& img': { maxWidth: '100%', height: 'auto', borderRadius: '4px', my: 2 },
                            // Updated Link styles: bright blue, no underline (underline on hover)
                            '& a': { 
                                color: '#29b6f6', // Bright blue (Material UI Light Blue 400)
                                textDecoration: 'none', 
                                '&:hover': { 
                                    textDecoration: 'underline' 
                                }
                            },
                            // Heading styles
                            '& h1, & h2, & h3, & h4, & h5, & h6': { mt: 3, mb: 1.5, textAlign: 'left' } 
                        }}
                     />
                     {originalUrl && (
                        <Typography variant="caption" sx={{ display: 'block', mt: 4, fontStyle: 'italic', textAlign: 'center' }}>
                            Original article source: <MuiLink href={originalUrl} target="_blank" rel="noopener noreferrer">{originalUrl}</MuiLink>
                        </Typography>
                     )}
                 </Box>
            )}
        </Paper>
    );
};

export default ArticleViewer; 